import { Palette, Sparkles, Star, MousePointer2, SlidersHorizontal, RotateCcw } from "lucide-react";
import {
  CURSOR_MODES,
  DEFAULT_THEME_SETTINGS,
  EFFECT_MODES,
  THEME_PRESETS,
} from "../config/themeConfig";
import "../styles/views/settings-view.css";

function formatMultiplier(value) {
  return `${Number(value).toFixed(1)}x`;
}

function SettingsView({ settings, onChange, onReset }) {
  return (
    <>
      <section className="glass-panel panel-block settings-hero">
        <div className="settings-hero-head">
          <div>
            <p className="settings-kicker">
              <SlidersHorizontal size={14} />
              Theme Studio
            </p>
            <h3 className="settings-title">Customize your galaxy</h3>
            <p className="settings-subtitle">
              Create your own listening atmosphere with color, stars, effects, and cursor style.
            </p>
          </div>

          <button
            type="button"
            className="settings-reset-btn"
            onClick={() => onReset(DEFAULT_THEME_SETTINGS)}
          >
            <RotateCcw size={14} />
            Reset
          </button>
        </div>
      </section>

      <section className="glass-panel panel-block settings-section">
        <div className="section-inline">
          <h3 className="section-title settings-section-title">
            <Palette size={16} />
            Background Palette
          </h3>
        </div>
        <div className="settings-preset-grid">
          {THEME_PRESETS.map((preset) => {
            const isActive = preset.id === settings.presetId;
            return (
              <button
                key={preset.id}
                type="button"
                className={`settings-preset-card${isActive ? " is-active" : ""}`}
                onClick={() => onChange({ presetId: preset.id })}
              >
                <div
                  className="settings-preset-preview"
                  style={{
                    background: `linear-gradient(135deg, ${preset.colors.bgLinearStart}, ${preset.colors.bgLinearMid} 56%, ${preset.colors.bgLinearEnd})`,
                  }}
                >
                  <span
                    className="settings-preset-dot"
                    style={{ background: preset.colors.neonCyan }}
                  />
                  <span
                    className="settings-preset-dot"
                    style={{ background: preset.colors.neonPurple }}
                  />
                  <span
                    className="settings-preset-dot"
                    style={{ background: preset.colors.neonPink }}
                  />
                </div>
                <p className="settings-preset-name">{preset.name}</p>
                <p className="settings-preset-description">{preset.description}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="glass-panel panel-block settings-section">
        <div className="section-inline">
          <h3 className="section-title settings-section-title">
            <Star size={16} />
            Stars & Nebula
          </h3>
        </div>

        <div className="settings-slider-grid">
          <label className="settings-control">
            <span>Star Density</span>
            <strong>{formatMultiplier(settings.starDensity)}</strong>
            <input
              type="range"
              min="0.4"
              max="2.4"
              step="0.1"
              value={settings.starDensity}
              onChange={(event) => onChange({ starDensity: Number(event.target.value) })}
            />
          </label>

          <label className="settings-control">
            <span>Star Speed</span>
            <strong>{formatMultiplier(settings.starSpeed)}</strong>
            <input
              type="range"
              min="0.6"
              max="2.4"
              step="0.1"
              value={settings.starSpeed}
              onChange={(event) => onChange({ starSpeed: Number(event.target.value) })}
            />
          </label>

          <label className="settings-control">
            <span>Twinkle Intensity</span>
            <strong>{formatMultiplier(settings.twinkleIntensity)}</strong>
            <input
              type="range"
              min="0.6"
              max="2.2"
              step="0.1"
              value={settings.twinkleIntensity}
              onChange={(event) => onChange({ twinkleIntensity: Number(event.target.value) })}
            />
          </label>

          <label className="settings-control">
            <span>Nebula Intensity</span>
            <strong>{formatMultiplier(settings.nebulaIntensity)}</strong>
            <input
              type="range"
              min="0.4"
              max="1.9"
              step="0.1"
              value={settings.nebulaIntensity}
              onChange={(event) => onChange({ nebulaIntensity: Number(event.target.value) })}
            />
          </label>

          <label className="settings-control">
            <span>FX Intensity</span>
            <strong>{formatMultiplier(settings.effectIntensity)}</strong>
            <input
              type="range"
              min="0.5"
              max="2.2"
              step="0.1"
              value={settings.effectIntensity}
              onChange={(event) => onChange({ effectIntensity: Number(event.target.value) })}
            />
          </label>
        </div>
      </section>

      <section className="glass-panel panel-block settings-section">
        <div className="settings-chip-section">
          <article>
            <h3 className="section-title settings-section-title">
              <Sparkles size={16} />
              Visual Effects
            </h3>
            <div className="settings-chip-row">
              {EFFECT_MODES.map((mode) => {
                const isActive = settings.effectMode === mode.id;
                return (
                  <button
                    key={mode.id}
                    type="button"
                    className={`settings-chip-btn${isActive ? " is-active" : ""}`}
                    onClick={() => onChange({ effectMode: mode.id })}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
          </article>

          <article>
            <h3 className="section-title settings-section-title">
              <MousePointer2 size={16} />
              Cursor
            </h3>
            <div className="settings-chip-row">
              {CURSOR_MODES.map((cursor) => {
                const isActive = settings.cursorMode === cursor.id;
                return (
                  <button
                    key={cursor.id}
                    type="button"
                    className={`settings-chip-btn${isActive ? " is-active" : ""}`}
                    onClick={() => onChange({ cursorMode: cursor.id })}
                  >
                    {cursor.label}
                  </button>
                );
              })}
            </div>
          </article>
        </div>
      </section>
    </>
  );
}

export default SettingsView;
